import { MultilingualLanguages } from '@asgard/api-common';
import HtmlTrans from 'components/shared/HtmlTrans';
import { LoggedUserContext } from 'context/logged-user/loggeduser.context';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts/useToasts';
import { UIOfferListItem } from 'interfaces/ui/ui-offer-list-item';
import isEqual from 'lodash/isEqual';
import { FetchError } from 'model';
import { ManfrediteOfferAlert } from 'model/model-manfredite-offer-alert';
import { OffersListFilter } from 'model/model-offers-list-filter';
import { useRouter } from 'next/dist/client/router';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import OfferAlertService from 'services/service-offer-alert';
import ServiceEventTracking from 'services/service.event-tracking';
import { ServiceWebOffer } from 'services/service.web-offer';
import { useDebouncedCallback } from 'use-debounce';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import {
  getFilterFromUrlParams,
  setUrlQueryParamsFromJobOffersFilter
} from 'utils/job-offers-filter';
import { getLocale } from 'utils/localize.utils';
import { removeQueryParamsFromRouter } from 'utils/router';
import { normalizeToOfferAlertCreateRequest } from './filters/Filters.adapter';
import {
  DEBOUNCE_FILTER_OFFERS_TIME_IN_MILLIS,
  DEFAULT_FILTER,
  LOADING_AFTER_MILLIS,
  OPEN_ALERT_PARAM,
  UNSUSCRIBE_ALERT_PARAM
} from './JobOffersListComponent.constants';
import {
  UseJobOffersAlerts,
  UseJobOffersList
} from './JobOffersListComponent.types';
import {
  checkIsDefaultFilter,
  getTextForOffersListFilter,
  hasFiltersInUrlParams
} from './JobOffersListComponent.utils';

export const useJobOffersList: UseJobOffersList = ({
  languages,
  allLanguages,
  techs
}) => {
  const router = useRouter();

  const { t } = useI18n('job-offer-list');
  const { locale } = router;

  const [currentFilters, setCurrentFilters] =
    useState<OffersListFilter>(DEFAULT_FILTER);

  const [UIOffers, setUIOffers] = useState<UIOfferListItem[]>([]);

  const [currentFiltersText, setCurrentFiltersText] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeFilters, setActiveFilters] = useState<boolean>(false);
  const [filtersDesktop, setFiltersDesktop] = useState<boolean>(true);

  const isDefaultFilter = useMemo(
    () => checkIsDefaultFilter(currentFilters),
    [currentFilters]
  );

  const filterOffersDebounced = useDebouncedCallback(
    async (filter: OffersListFilter) => {
      setupFilterText(filter);

      const loadingDelay = setTimeout(() => {
        setLoading(true);
      }, LOADING_AFTER_MILLIS);

      const filteredOffers = await ServiceWebOffer.getAllSummary(
        filter,
        locale?.toUpperCase() as MultilingualLanguages
      );

      setUrlQueryParamsFromJobOffersFilter({
        filter,
        router
      });
      clearTimeout(loadingDelay);
      setUIOffers(filteredOffers);
      setLoading(false);
    },
    DEBOUNCE_FILTER_OFFERS_TIME_IN_MILLIS
  );

  const handleOnFilterChange = async (
    filter: OffersListFilter
  ): Promise<void> => {
    if (filter.minSalary && +filter.minSalary < 0) {
      return;
    }

    setCurrentFilters(filter);
    filterOffersDebounced(filter);
  };

  const handleOnFilterReset = async (): Promise<void> => {
    handleOnFilterChange(DEFAULT_FILTER);
  };

  function setupFilterText(filter: OffersListFilter): void {
    const text = getTextForOffersListFilter({
      filter,
      t,
      languages,
      allMinLanguages: allLanguages,
      techs,
      locale
    });

    setCurrentFiltersText(text);
  }

  useEffect(() => {
    function isFiltersDesktop(): void {
      setFiltersDesktop(window.matchMedia('(min-width: 1056px)').matches);
    }

    isFiltersDesktop();

    window.addEventListener('resize', isFiltersDesktop);

    return () => {
      window.removeEventListener('resize', isFiltersDesktop);
    };
  }, []);

  useEffect(() => {
    const checkQueryParams = async (): Promise<void> => {
      const urlSearchParams = new URLSearchParams(window.location.search);

      ServiceEventTracking.track({
        type: 'Offers - List Viewed',
        properties: {
          pageLanguage: locale
        }
      });

      if (hasFiltersInUrlParams(urlSearchParams)) {
        const filtersFromUrl = getFilterFromUrlParams(urlSearchParams);
        const isDefaultFilter = checkIsDefaultFilter(filtersFromUrl);

        setCurrentFilters(filtersFromUrl);

        if (!isDefaultFilter) {
          setupFilterText(filtersFromUrl);
        }

        handleOnFilterChange(filtersFromUrl);
      } else {
        setCurrentFilters(DEFAULT_FILTER);

        await setUrlQueryParamsFromJobOffersFilter({
          filter: DEFAULT_FILTER,
          router
        });

        handleOnFilterChange(DEFAULT_FILTER);
      }
    };

    checkQueryParams();
  }, []);

  return {
    t,
    UIOffers,
    currentFilters,
    currentFiltersText,
    loading,
    activeFilters,
    setActiveFilters,
    filtersDesktop,
    isDefaultFilter,
    handleOnFilterChange,
    handleOnFilterReset
  };
};

export const useJobOffersAlerts: UseJobOffersAlerts = ({ currentFilters }) => {
  const { t } = useI18n('job-offer-list');
  const router = useRouter();
  const { locale } = router;

  const { user } = useContext(LoggedUserContext);
  const { addErrorToast, addSuccessToast } = useToasts();
  const [alerts, setAlerts] = useState<ManfrediteOfferAlert[]>([]);
  const [isManageAlertsModalOpened, setIsManageAlertsModalOpened] =
    useState(false);
  const [unsuscribeActionOfferAlert, setUnsuscribeActionOfferAlert] = useState<
    string | null
  >(null);

  const [isConfirmSaveModalOpened, setIsConfirmSaveModalOpened] =
    useState(false);
  const [isSavingAlert, setIsSavingAlert] = useState(false);

  // Alerts can only be created if some "saveable" filters are applied
  // (meaning that they are different from the default filters)
  // currency and onlyActive are not considered "saveable" filters.
  const userCanCreateAlerts = useMemo(
    () =>
      !isEqual(
        {
          locations: currentFilters.locations || DEFAULT_FILTER.locations,
          minLanguages:
            currentFilters.minLanguages || DEFAULT_FILTER.minLanguages,
          minSalary: currentFilters.minSalary || DEFAULT_FILTER.minSalary,
          offerLanguage:
            currentFilters.offerLanguage || DEFAULT_FILTER.offerLanguage,
          remoteOptions:
            currentFilters.remoteOptions || DEFAULT_FILTER.remoteOptions,
          techRoles: currentFilters.techRoles || DEFAULT_FILTER.techRoles,
          techs: currentFilters.techs || DEFAULT_FILTER.techs,
          text: currentFilters.text || DEFAULT_FILTER.text,
          currency: undefined,
          onlyActive: undefined
        },
        {
          ...DEFAULT_FILTER,
          currency: undefined,
          onlyActive: undefined
        }
      ),
    [
      currentFilters.locations,
      currentFilters.minLanguages,
      currentFilters.minSalary,
      currentFilters.offerLanguage,
      currentFilters.remoteOptions,
      currentFilters.techRoles,
      currentFilters.techs,
      currentFilters.text
    ]
  );

  useEffect(() => {
    async function fetchAlerts(): Promise<void> {
      if (!user) {
        return;
      }

      try {
        const manfrediteOfferAlerts = await OfferAlertService.getAll(
          getLocale(locale)
        );

        setAlerts(manfrediteOfferAlerts);
      } catch (ex) {
        if (ex instanceof FetchError && ex.statusCode >= 500) {
          console.error(
            `Could not get Manfredite alerts. Exception: ${ex}`,
            ex
          );
        }

        setAlerts([]);
      }
    }

    fetchAlerts();
  }, [user]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);

    if (hasManageOfferAlerts(urlSearchParams)) {
      setIsManageAlertsModalOpened(true);

      setTimeout(() => {
        removeQueryParamsFromRouter(router, [OPEN_ALERT_PARAM]);
      }, 500);
    }

    const offerAlertToRemove = getOfferAlertToRemove(urlSearchParams);

    if (offerAlertToRemove) {
      setIsManageAlertsModalOpened(true);
      setUnsuscribeActionOfferAlert(offerAlertToRemove);

      setTimeout(() => {
        removeQueryParamsFromRouter(router, [
          OPEN_ALERT_PARAM,
          UNSUSCRIBE_ALERT_PARAM
        ]);
      }, 500);
    }
  }, []);

  function getOfferAlertToRemove(params: URLSearchParams): string | null {
    return params.get(UNSUSCRIBE_ALERT_PARAM);
  }

  function hasManageOfferAlerts(params: URLSearchParams): boolean {
    return (
      (params.get(OPEN_ALERT_PARAM) !== null &&
        params.get(OPEN_ALERT_PARAM) !== '') ||
      (params.get(UNSUSCRIBE_ALERT_PARAM) !== null &&
        params.get(UNSUSCRIBE_ALERT_PARAM) !== '')
    );
  }

  const handleOpenManageAlertsClick = useCallback(() => {
    setIsManageAlertsModalOpened(true);
  }, []);

  const handleSignupClick = useCallback(async (): Promise<void> => {
    const params = new URLSearchParams(window.location.search);

    params.set('action', 'create-alert');

    const returnUrl = `/${locale}${getAppUrl(
      APP_URLS.jobOffers,
      locale
    )}?${params}`;

    const signupPath = `${getAppUrl(
      APP_URLS.join,
      locale
    )}?returnUrl=${encodeURIComponent(returnUrl)}`;

    const url = `${window.location.origin}/${locale}${signupPath}`;

    window.location.replace(url);
  }, [locale]);

  const handleCreateAlertClick = useCallback(async (): Promise<void> => {
    try {
      setIsSavingAlert(true);

      const savedAlert = await OfferAlertService.save(
        getLocale(locale),
        normalizeToOfferAlertCreateRequest(currentFilters, getLocale(locale))
      );

      setAlerts([...alerts, savedAlert]);

      addSuccessToast(
        user?.configuration?.availabilityStatus === 'NOT AVAILABLE' ? (
          <HtmlTrans
            ns="job-offer-list"
            i18nKey="messages.success.save-alert-desc-availability"
            components={{
              availabilityLink: (
                <a
                  href={getAppUrl(APP_URLS.portalV2Contact, router.locale)}
                  target="_blank"
                  rel="noreferrer"
                />
              )
            }}
          />
        ) : (
          <HtmlTrans
            ns="job-offer-list"
            i18nKey="messages.success.save-alert-desc"
          />
        ),
        t('messages.success.save-alert')
      );

      setIsSavingAlert(false);
    } catch (ex) {
      if (ex instanceof FetchError && ex.statusCode >= 500) {
        console.error(`Could not save Manfredite alert. Exception: ${ex}`, ex);
      }

      addErrorToast(t('messages.error.save-alert'));
      setIsSavingAlert(false);
    }
  }, [
    addErrorToast,
    addSuccessToast,
    alerts,
    currentFilters,
    locale,
    router.locale,
    t,
    user?.configuration?.availabilityStatus
  ]);

  useEffect(() => {
    if (router.query?.action === 'create-alert') {
      setIsConfirmSaveModalOpened(true);

      setTimeout(() => {
        removeQueryParamsFromRouter(router, ['action']);
      }, 500);
    }
  }, [router]);

  return {
    alerts,
    setAlerts,
    isConfirmSaveModalOpened,
    setIsConfirmSaveModalOpened,
    isSavingAlert,
    isManageAlertsModalOpened,
    setIsManageAlertsModalOpened,
    unsuscribeActionOfferAlert,
    handleOpenManageAlertsClick,
    handleCreateAlertClick,
    handleSignupClick,
    userCanCreateAlerts
  };
};
